// deployment_class: customer_env_allowed
/**
 * Turning "here is a folder" into "here are the documents in it".
 *
 * An agency does not hand you four tidy files. It hands you a directory —
 * nested, with a decade of `.DS_Store`, scanned images, zip archives and
 * `~$budget.xlsx` lock files alongside the things that matter. Uploading all of
 * it would fail eighty times to succeed twenty; uploading a silent subset would
 * be worse, because the CEO would never learn what we skipped.
 *
 * So this walks the folder, splits it into what we will read and what we will
 * not, and hands BOTH back. The caller shows both.
 */

/**
 * Extensions the extraction layer will attempt.
 *
 * MIRROR — the authority is `SUPPORTED_EXTENSIONS` in
 * `src/v3/superconductor/extraction/extract.ts`, which derives it from the
 * extractor routing sets. Equality is pinned by
 * `web/v3/test/novello/import-accepts.contract.test.ts`, so this list cannot
 * drift narrower than what the server can actually read — which it already had:
 * the picker offered four extensions while the server read seven, so a `.docm`
 * the Superconductor handles fine was greyed out in the dialog.
 */
const SUPPORTED_EXTENSIONS: ReadonlySet<string> = new Set([
  'pdf',
  'xlsx',
  'xlsm',
  'xltx',
  'xltm',
  'docx',
  'docm',
  'dotx',
]);

/**
 * The `accept` attribute for a file dialog, derived so it cannot fall behind.
 *
 * This — not the set behind it — is what the contract test compares against the
 * backend, because this is the string the picker actually ships.
 */
export const ACCEPT_ATTRIBUTE = [...SUPPORTED_EXTENSIONS].map((e) => `.${e}`).join(',');

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Files an operating system creates and nobody meant to send: Finder metadata,
 * AppleDouble resource forks, Office lock files. Skipping these SILENTLY is the
 * one case where silence is right — the user did not put them there and listing
 * twelve `.DS_Store` entries as "skipped" buries the one skip that matters.
 */
function isSystemNoise(name: string): boolean {
  return name === '.DS_Store' || name === 'Thumbs.db' || name.startsWith('._') || name.startsWith('~$');
}

export interface FolderScan {
  /** In folder order, so the list on screen matches the list on disk. */
  readonly accepted: readonly File[];
  /** Named, not counted — "25 skipped" tells the CEO nothing actionable. */
  readonly skipped: readonly { readonly name: string; readonly reason: string }[];
}

/** Split a flat set of files into what we will read and what we will not. */
export function triage(files: readonly File[]): FolderScan {
  const accepted: File[] = [];
  const skipped: { name: string; reason: string }[] = [];

  for (const file of files) {
    if (isSystemNoise(file.name)) continue;
    const ext = extensionOf(file.name);
    if (!SUPPORTED_EXTENSIONS.has(ext)) {
      skipped.push({
        name: file.name,
        reason: ext === '' ? 'no file type' : `.${ext} is not a format Kathedral reads`,
      });
      continue;
    }
    if (file.size === 0) {
      skipped.push({ name: file.name, reason: 'the file is empty' });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, skipped };
}

// ── Reading a dropped directory ──────────────────────────────────────────────
//
// Dropping a FOLDER yields no files at all through `dataTransfer.files`; the
// directory has to be walked through the entries API. Two things bite here and
// both are silent:
//
//   1. `webkitGetAsEntry()` must be called SYNCHRONOUSLY in the drop handler.
//      The DataTransferItemList is emptied the moment the handler yields, so an
//      `await` before the call leaves you holding nulls.
//   2. `readEntries()` returns a PAGE, not a directory. Chrome caps it near 100
//      and signals the end with an empty array. Calling it once reads the first
//      hundred files and quietly loses the rest — which is exactly the size of
//      folder this feature exists for.

interface FileSystemEntryLike {
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly name: string;
  file?(onSuccess: (file: File) => void, onError: (err: unknown) => void): void;
  createReader?(): {
    readEntries(
      onSuccess: (entries: FileSystemEntryLike[]) => void,
      onError: (err: unknown) => void,
    ): void;
  };
}

/** Capture entries before the event is recycled. Must run synchronously. */
export function entriesFromDrop(dataTransfer: DataTransfer): FileSystemEntryLike[] {
  const items = Array.from(dataTransfer.items ?? []);
  return items
    .map((item) =>
      typeof (item as unknown as { webkitGetAsEntry?: unknown }).webkitGetAsEntry === 'function'
        ? ((item as unknown as { webkitGetAsEntry(): FileSystemEntryLike | null }).webkitGetAsEntry())
        : null,
    )
    .filter((entry): entry is FileSystemEntryLike => entry !== null);
}

function fileOf(entry: FileSystemEntryLike): Promise<File | null> {
  return new Promise((resolve) => {
    if (typeof entry.file !== 'function') return resolve(null);
    entry.file(
      (file) => resolve(file),
      () => resolve(null),
    );
  });
}

/**
 * How much of a folder one import will take.
 *
 * These are LOOP GUARDS, not a product limit on how much an agency may import.
 * A folder that hits either of them stops the walk and SAYS SO — a cap that
 * truncates quietly reads to the user as "everything landed", which is the one
 * thing an import must never imply.
 */
const MAX_FOLDER_FILES = 5_000;
const MAX_FOLDER_ENTRIES = 50_000;

/** Shared across the whole walk, so one pathological directory cannot spend it all. */
interface Budget {
  files: number;
  entries: number;
  readonly maxFiles: number;
  readonly maxEntries: number;
  stopped: 'files' | 'entries' | null;
}

function spent(b: Budget): boolean {
  if (b.files >= b.maxFiles) {
    b.stopped ??= 'files';
    return true;
  }
  if (b.entries >= b.maxEntries) {
    b.stopped ??= 'entries';
    return true;
  }
  return false;
}

/**
 * One page of a directory listing.
 *
 * `readEntries()` returns a PAGE, not a directory — Chrome caps it near 100 and
 * signals the end with an empty array. Reading it once loses everything past
 * the hundredth file, which is the exact size of folder this feature exists
 * for. A directory we cannot read yields empty rather than failing the import.
 */
function nextPage(reader: {
  readEntries(
    onSuccess: (entries: FileSystemEntryLike[]) => void,
    onError: (err: unknown) => void,
  ): void;
}): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve) => {
    reader.readEntries(
      (page) => resolve(page),
      () => resolve([]),
    );
  });
}

export interface FolderWalk {
  readonly files: readonly File[];
  /**
   * Null when the folder was read in full. Otherwise why the walk stopped, so
   * the caller can tell the user there is more than what they are looking at.
   */
  readonly stopped: 'files' | 'entries' | null;
}

/**
 * Walk dropped entries into a flat file list, descending into subfolders.
 *
 * Pages are STREAMED rather than drained first: a directory is processed as its
 * listing arrives, so the budget is spent reading documents instead of
 * enumerating a folder we will not finish. The `await` between pages is also
 * what keeps the call stack flat — `readEntries` may call back synchronously,
 * and a plain recursive drain grows a frame per page until the tab dies.
 *
 * `depthLimit` breaks symlink cycles — a directory containing itself would
 * otherwise recurse forever.
 */
export async function filesFromEntries(
  entries: readonly FileSystemEntryLike[],
  options: {
    readonly depthLimit?: number;
    readonly maxFiles?: number;
    readonly maxEntries?: number;
  } = {},
): Promise<FolderWalk> {
  const { depthLimit = 12, maxFiles = MAX_FOLDER_FILES, maxEntries = MAX_FOLDER_ENTRIES } = options;
  const budget: Budget = { files: 0, entries: 0, maxFiles, maxEntries, stopped: null };
  const out: File[] = [];

  async function walk(entry: FileSystemEntryLike, depth: number): Promise<void> {
    if (spent(budget) || depth > depthLimit) return;

    if (entry.isFile) {
      const file = await fileOf(entry);
      // A file we cannot open — locked, permission denied — is passed over
      // rather than sinking the batch around it.
      if (file) {
        out.push(file);
        budget.files += 1;
      }
      return;
    }

    if (!entry.isDirectory) return;
    const reader = entry.createReader?.();
    if (!reader) return;

    for (;;) {
      const page = await nextPage(reader);
      // An empty page is the ONLY end-of-directory signal there is.
      if (page.length === 0) return;
      budget.entries += page.length;
      for (const child of page) {
        if (spent(budget)) return;
        await walk(child, depth + 1);
      }
      if (spent(budget)) return;
    }
  }

  for (const entry of entries) {
    budget.entries += 1;
    await walk(entry, 0);
  }

  return { files: out, stopped: budget.stopped };
}
